import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { fetchLuxPlusPreferredPaymentCard, fetchMemberSubscription, fetchProductSubscription } from 'actions/LuxPlusActions'
import LuxPlusNonMemberLandingPageContent from 'luxPlus/components/LandingPage/LuxPlusNonMemberLandingPageContent'
import LuxPlusLoggedInLandingPageContent from 'luxPlus/components/LandingPage/LuxPlusLoggedInLandingPageContent'
import LuxPlusFailedPaymentLandingPageContent from 'luxPlus/components/LandingPage/LuxPlusFailedPaymentLandingPageContent'
import LuxPlusFreePreviewLandingPageContent from 'luxPlus/components/LandingPage/LuxPlusFreePreviewLandingPageContent'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import useHashSignal from 'hooks/useHashSignal'
import useQueryParams from 'hooks/useQueryParams'
import { LUXURY_PLUS, LUX_PLUS_PROMOTIONAL_OFFER_PARAM, LUXURY_PLUS_PATHS, LUX_PLUS_MANAGE_ACCOUNT_HASH } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits, getIsFreePreviewLuxPlusMember, getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { replaceWithRegion } from 'actions/NavigationActions'
import getLuxPlusShouldRedirectToAccountPage from 'luxPlus/selectors/getLuxPlusShouldRedirectToAccountPage'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { isActiveMemberWithSubscriptionTier } from 'luxPlus/lib/utils'
import usePartnerPromoOffers from 'luxPlus/hooks/usePartnerPromoOffers'
import { PARTNER_PROMO_SLUG_TO_CLASSIFIER } from 'luxPlus/constants/partnerPromo'
import LuxPlusFlightVoucherModal from 'luxPlus/components/modals/LuxPlusFlightVoucherModal'
import LuxPlusManageAccountModal from 'luxPlus/components/LuxPlusManageAccount/LuxPlusManageAccountModal'

function LuxPlusLandingPage() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const queryParams = useQueryParams()

  // Check if the loyalty luxplus landing page feature flag is enabled
  const isLoyaltyLuxplusEnabled = useFeatureFlag(OptimizelyFeatureFlags.loyaltyLuxplusLandingPage)

  const isLoggedIn = useAppSelector(selectLoggedIn)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchLuxPlusPreferredPaymentCard())
  }, [dispatch])

  const shouldRedirectToAccountPage = useAppSelector(getLuxPlusShouldRedirectToAccountPage)

  // Show snackbar if user is on landing page with a benefit param but can't access it
  // (they were likely redirected from the account page or clicked a benefit link)
  useEffect(() => {
    // Old flow: members are redirected to AccountPage which has its own benefit handler
    if (shouldRedirectToAccountPage && !isLoyaltyLuxplusEnabled) return

    const benefitValue = queryParams.get('benefit')
    if (!benefitValue) return

    if (benefitValue === 'flight-voucher') {
      showModal(<LuxPlusFlightVoucherModal />)
    } else if (!shouldRedirectToAccountPage) {
      showSnackbar(
        intl.formatMessage(
          {
            id: 'TiF8HQ2vl1Zz7f3zdv_4',
            defaultMessage: 'Become a full {programName} member to access partner-only deals and benefits.',
            description: 'Snackbar message shown when a non-member tries to access a LuxPlus benefit from the landing page',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        ),
        'neutral',
        {
          heading: intl.formatMessage({
            id: 'LCD0nXWOQKq9-GIgbd8C',
            defaultMessage: 'Membership required',
            description: 'Snackbar heading when a non-member tries to access a LuxPlus benefit',
          }),
        },
      )
    }
  }, [queryParams, shouldRedirectToAccountPage, isLoyaltyLuxplusEnabled, intl, showModal])
  const orderId = queryParams.get('orderId')

  // Post-purchase: poll for subscription to become active, matching LuxPlusAccountPage behaviour
  useEffect(() => {
    if (!isLoyaltyLuxplusEnabled || !orderId) return
    dispatch(fetchMemberSubscription({ refetch: true, poll: true, validate: isActiveMemberWithSubscriptionTier }))
    showSnackbar(
      intl.formatMessage(
        {
          id: 'AOoTvLFH8TgeAtedjNnf',
          defaultMessage: 'You are now a {programName} member!',
          description: 'Snackbar success message after completing LuxPlus membership purchase',
        },
        { programName: LUXURY_PLUS.PROGRAM_NAME },
      ),
      'success',
    )
  }, [isLoyaltyLuxplusEnabled, orderId, dispatch, intl])

  useEffect(() => {
    if (isLoyaltyLuxplusEnabled) return
    if (!shouldRedirectToAccountPage) return
    const queryString = window.location.search
    dispatch(replaceWithRegion(`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}${queryString}`))
  }, [dispatch, shouldRedirectToAccountPage, isLoyaltyLuxplusEnabled])

  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isPendingRenewal = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const isFreePreview = useAppSelector(getIsFreePreviewLuxPlusMember)
  const hasPromotionParam = queryParams.get(LUX_PLUS_PROMOTIONAL_OFFER_PARAM) === 'true'

  const isLuxPlusPromotionalOfferEnabled = !isLuxPlusMember && hasPromotionParam

  const purchasePromotionalLuxPlusOffer = useCallback(() => {
    if (hasPromotionParam && !isLoggedIn) {
      openLoginModal()
      return
    }
    if (!isLuxPlusPromotionalOfferEnabled) return
    dispatch(purchaseLuxPlus({ hasJoinFee: false, replaceCurrentWith: `/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}` }))
  }, [hasPromotionParam, isLuxPlusPromotionalOfferEnabled, isLoggedIn, openLoginModal, dispatch])

  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)

  useEffect(() => {
    purchasePromotionalLuxPlusOffer()
  }, [purchasePromotionalLuxPlusOffer, subscriptionProduct.offers])

  const { hasHash: hasManageAccountHash } = useHashSignal({
    hash: LUX_PLUS_MANAGE_ACCOUNT_HASH,
  })
  const isAuthInitComplete = useAppSelector(state => state.auth.initComplete)
  const handledManageAccountHashRef = useRef(false)

  // The #manage-account hash can be present on a cold load (e.g. from an email
  // link) before the cookie/token check and the member's subscription have
  // resolved. Deciding once on mount would flash the login modal (isLoggedIn is
  // briefly false) and miss the member case (isLuxPlusMember is still false), so
  // we wait for auth to settle, then re-evaluate as membership resolves, acting
  // once via the ref guard.
  useEffect(() => {
    if (!hasManageAccountHash || handledManageAccountHashRef.current) return
    if (!isAuthInitComplete) return
    if (isLuxPlusMember) {
      handledManageAccountHashRef.current = true
      showModal(<LuxPlusManageAccountModal />)
    } else if (!isLoggedIn) {
      handledManageAccountHashRef.current = true
      openLoginModal()
    }
    // logged in but membership not yet resolved → wait; re-runs when isLuxPlusMember flips
  }, [hasManageAccountHash, isAuthInitComplete, isLuxPlusMember, isLoggedIn, showModal, openLoginModal])

  const partnerPromos = usePartnerPromoOffers()
  const benefitSlug = queryParams.get('benefit')
  const isPopupShownRef = useRef(false)

  useEffect(() => {
    if (!benefitSlug || !isLuxPlusMember || isPopupShownRef.current) return
    const classifier = PARTNER_PROMO_SLUG_TO_CLASSIFIER[benefitSlug]
    if (!classifier) return
    const promo = partnerPromos.find(p => p.id === classifier)
    if (!promo?.onViewDetails) return
    isPopupShownRef.current = true
    promo.onViewDetails()
  }, [benefitSlug, isLuxPlusMember, partnerPromos])

  if (isLuxPlusMember) return <LuxPlusLoggedInLandingPageContent />
  if (isPendingRenewal) return <LuxPlusFailedPaymentLandingPageContent />
  if (isFreePreview) return <LuxPlusFreePreviewLandingPageContent />

  return <LuxPlusNonMemberLandingPageContent />
}

export default LuxPlusLandingPage
